import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { AUTH_URL } from 'src/configs';
import request from 'src/request';

/** One entry per filter bar, keyed by the API query parameter it sends. */
export type UserFilters = Record<string, string>;

interface UsersState {
  data: {
    users: object[];
    totalCount: number;
    totalPages: number;
    filters: UserFilters;
  };
  loading: boolean;
  error: string | null;
}

const initialState: UsersState = {
  data: {
    users: [],
    totalCount: 0,
    totalPages: 0,
    filters: {},
  },
  loading: false,
  error: null,
};

export const fetchUsers = createAsyncThunk(
  'components/fetchUsers',
  async ({
    page,
    filters,
    sort,
    order,
  }: {
    page: number;
    /** Already debounced and trimmed; each key is an API param, ANDed server-side. */
    filters?: UserFilters;
    sort?: string;
    order?: string;
  }) => {
    const query = new URLSearchParams({ page: String(page), limit: '10' });
    Object.entries(filters ?? {}).forEach(([key, value]) => {
      if (value) query.set(key, value);
    });
    if (sort && order) {
      query.set('sort', sort);
      query.set('order', order);
    }

    const response = await request.get(`${AUTH_URL}/v1/admin/users?${query.toString()}`);
    return {
      data: response.data.users,
      totalCount: response.data.meta.total_count,
      totalPages: response.data.meta.page_count,
      page,
    };
  },
);

const usersData = createSlice({
  name: 'users',
  initialState,
  reducers: {
    updateFilter: (state, action: { payload: { key: string; value: string } }) => {
      const { key, value } = action.payload;
      state.data.filters = { ...state.data.filters, [key]: value };
    },
    resetFilters: (state) => {
      state.data.filters = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        // Filters are owned by updateFilter/resetFilters; a resolved request
        // must not overwrite what the admin has typed since it was fired.
        state.data.users = action.payload.data;
        state.data.totalCount = action.payload.totalCount;
        state.data.totalPages = action.payload.totalPages;
        state.loading = false;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Failed to fetch users';
      });
  },
});

export const { updateFilter, resetFilters } = usersData.actions;

export default usersData.reducer;
